interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
}

interface N8nWorkflow {
  id: number;
  name: string;
  active: boolean;
  nodes: Array<{
    parameters: Record<string, any>;
    name: string;
    type: string;
    position: [number, number];
    typeVersion: number;
    id: string;
  }>;
  connections: Record<string, any>;
}

class GeminiService {
  private apiKey: string;
  private baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent';

  constructor() {
    this.apiKey = import.meta.env.VITE_GEMINI_API_KEY || '';
    console.log('🔑 Environment check:', {
      hasViteApiKey: !!import.meta.env.VITE_GEMINI_API_KEY,
      keyLength: this.apiKey.length,
      keyPrefix: this.apiKey.substring(0, 10) + '...'
    });
  }

  private createN8nPrompt(userPrompt: string): string {
    return `You are an expert n8n workflow automation engineer. Your task is to convert natural language automation requests into valid n8n workflow JSON.

IMPORTANT: You must respond with ONLY a valid JSON object, no additional text or explanation.

User Request: "${userPrompt}"

Based on this request, generate a complete n8n workflow JSON with the following structure:
- Unique workflow ID and descriptive name
- Appropriate trigger node (Start, Webhook, Schedule, etc.)
- Processing nodes (Code, Set, If, etc.)
- Action nodes (Email, HTTP Request, etc.)
- Proper node connections
- Realistic node positioning

Common n8n node types to use:
- "n8n-nodes-base.start" - Start trigger
- "n8n-nodes-base.webhook" - Webhook trigger  
- "n8n-nodes-base.schedule" - Schedule trigger
- "n8n-nodes-base.code" - Code execution
- "n8n-nodes-base.set" - Set/Edit fields
- "n8n-nodes-base.if" - Conditional logic
- "n8n-nodes-base.httpRequest" - HTTP requests
- "n8n-nodes-base.emailSend" - Send emails
- "n8n-nodes-base.googleSheets" - Google Sheets
- "n8n-nodes-base.slack" - Slack integration
- "n8n-nodes-base.discord" - Discord integration

Generate a workflow that:
1. Starts with an appropriate trigger
2. Includes necessary data processing steps
3. Ends with the desired action
4. Has proper node connections
5. Uses realistic parameters based on the request

Return ONLY the JSON workflow object, no markdown formatting or additional text.`;
  }

  async generateWorkflow(prompt: string, apiKeyOverride?: string): Promise<N8nWorkflow> {
    const apiKey = apiKeyOverride || this.apiKey;
    
    if (!apiKey) {
      throw new Error('Gemini API key is not configured. Please add your VITE_GEMINI_API_KEY in the project settings.');
    }

    console.log('🤖 Generating n8n workflow with Gemini...');
    console.log('📝 User prompt:', prompt);

    const requestBody = {
      contents: [{
        parts: [{
          text: this.createN8nPrompt(prompt)
        }]
      }],
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048,
      }
    };

    try {
      const response = await fetch(`${this.baseUrl}?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Gemini API error:', errorData);
        
        if (response.status === 403) {
          throw new Error('Invalid API key or permission denied. Please check your Gemini API key.');
        } else if (response.status === 429) {
          throw new Error('API rate limit exceeded. Please try again in a few minutes.');
        } else {
          throw new Error(`Gemini API request failed: ${response.status} ${response.statusText}`);
        }
      }

      const data: GeminiResponse = await response.json();
      console.log('✅ Gemini API response received');

      if (!data.candidates || data.candidates.length === 0) {
        throw new Error('No response generated from Gemini API. Please try a different prompt.');
      }

      const generatedText = data.candidates[0].content.parts[0].text;
      console.log('🔄 Generated text from Gemini:', generatedText);

      // Parse the JSON response
      const cleanedJson = this.cleanJsonResponse(generatedText);
      const workflow: N8nWorkflow = JSON.parse(cleanedJson);

      console.log('✨ Successfully generated n8n workflow:', workflow.name);
      return workflow;

    } catch (error) {
      console.error('❌ Error generating workflow:', error);
      if (error instanceof SyntaxError) {
        throw new Error('Failed to parse generated workflow. The AI response was not valid JSON. Please try again.');
      }
      throw error;
    }
  }

  private cleanJsonResponse(text: string): string {
    // Remove any markdown formatting
    let cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    
    // Remove any leading/trailing whitespace
    cleaned = cleaned.trim();
    
    // Find the first { and last } to extract just the JSON
    const firstBrace = cleaned.indexOf('{');
    const lastBrace = cleaned.lastIndexOf('}');
    
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      cleaned = cleaned.substring(firstBrace, lastBrace + 1);
    }
    
    return cleaned;
  }

  // Test the API connection
  async testConnection(): Promise<boolean> {
    try {
      await this.generateWorkflow('Create a simple workflow that sends an email');
      return true;
    } catch (error) {
      console.error('❌ Gemini API connection test failed:', error);
      return false;
    }
  }
}

export const geminiService = new GeminiService();
export type { N8nWorkflow };

// Gemini AI Service for n8n Workflow Generation
// This service handles communication with Google's Gemini API
import { GoogleGenerativeAI } from '@google/generative-ai';